import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import * as PIXI from "pixi.js";
import { ASSETS } from "../../assets";
import { initializeStyles } from "../../utils/injectStyles";

export const loadAssets = createAsyncThunk(
  "assets/loadAssets",
  async (_, { rejectWithValue }) => {
    try {
      // Inject CSS styles and preload fonts
      await initializeStyles();

      // Wait for all web fonts to load before proceeding
      // This ensures PixiJS Text components can use custom fonts immediately
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      } else {
        // Fallback: wait a bit for fonts to start loading
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      // 🚀 PARALLEL LOADING - Load all textures simultaneously
      const texturePromises = ASSETS.textures.map(async (asset) => {
        try {
          // Add asset with alias so it can be retrieved by name
          PIXI.Assets.add({ alias: asset.name, src: asset.url });

          if (!PIXI.Assets.cache.has(asset.name)) {
            await PIXI.Assets.load(asset.name);
          }
        } catch (textureError) {
          // Log warning but don't crash - missing asset won't block other assets
          console.warn(
            `⚠️ Failed to load texture "${asset.name}" from ${asset.url}:`,
            textureError.message
          );
          console.warn(`   This asset is defined in assets.js but file is missing or inaccessible.`);
          // Return null to indicate failure but allow other assets to load
          return null;
        }
      });

      // 🚀 PARALLEL LOADING - Load all spine animations simultaneously
      const spinePromises = ASSETS.spineAnimations.map(async (animation) => {
        const baseName = animation.name;
        const jsonPath = animation.json;
        const atlasPath = jsonPath.replace(".json", ".atlas");

        // Validate paths before attempting to load
        if (!jsonPath || !atlasPath || !baseName) {
          throw new Error(
            `Invalid spine configuration for ${baseName}: missing path or name`
          );
        }

        // Check for malformed paths (should not contain undefined, null, HTML, or empty segments)
        const invalidPatterns = [
          "undefined",
          "null",
          "<!DOCTYPE",
          "<html",
          "DOCTYPE html",
          "window.__OPTIONS__",
          "response.ok",
          "Call the session",
        ];

        const hasInvalidPattern = invalidPatterns.some(
          (pattern) =>
            jsonPath.includes(pattern) ||
            atlasPath.includes(pattern) ||
            baseName.includes(pattern)
        );

        if (
          hasInvalidPattern ||
          jsonPath.endsWith("/.json") ||
          atlasPath.endsWith("/.atlas") ||
          !jsonPath.endsWith(".json") ||
          !atlasPath.endsWith(".atlas")
        ) {
          throw new Error(`Invalid spine path for ${baseName}: ${jsonPath}`);
        }

        // Final validation: ensure paths are valid URLs and don't contain HTML
        try {
          // Validate URL format
          const testUrl = new URL(jsonPath, window.location.origin);
          if (!testUrl.pathname.endsWith(".json")) {
            throw new Error(`Invalid JSON path format: ${jsonPath}`);
          }
        } catch (urlError) {
          // If URL construction fails, it's likely a relative path - validate manually
          if (
            !jsonPath.startsWith("/") &&
            !jsonPath.startsWith("./") &&
            !jsonPath.startsWith("http")
          ) {
            throw new Error(`Invalid path format: ${jsonPath}`);
          }
        }

        try {
          PIXI.Assets.add({ alias: `${baseName}-skeleton`, src: jsonPath });
          PIXI.Assets.add({ alias: `${baseName}-atlas`, src: atlasPath });

          if (!PIXI.Assets.cache.has(jsonPath)) {
            await PIXI.Assets.load([
              `${baseName}-skeleton`,
              `${baseName}-atlas`,
            ]);
          }
        } catch (spineError) {
          // Check if error message contains HTML (indicating a 404 HTML page was returned)
          const errorMessage = spineError.message || String(spineError);
          if (
            errorMessage.includes("<!DOCTYPE") ||
            errorMessage.includes("<html") ||
            errorMessage.includes("DOCTYPE html")
          ) {
            console.warn(
              `⚠️ Failed to load spine "${baseName}": Server returned HTML error page (file likely doesn't exist).`
            );
            console.warn(`   Path: ${jsonPath}`);
            console.warn(`   This asset is defined in assets.js but file is missing.`);
          } else {
            console.warn(
              `⚠️ Failed to load spine "${baseName}":`,
              spineError.message
            );
            console.warn(`   Path: ${jsonPath}`);
            console.warn(`   This asset is defined in assets.js but file is missing or inaccessible.`);
          }
          // Return null to indicate failure but allow other assets to load
          return null;
        }
      });

      // 🚀 PARALLEL LOADING - Load all bitmap fonts simultaneously
      const fontPromises = ASSETS.bitmapFonts.map(async (font) => {
        try {
          if (!PIXI.Assets.cache.has(font.json)) {
            await PIXI.Assets.load(font.json);
          }
        } catch (fontError) {
          console.warn(
            `⚠️ Failed to load font "${font.name}":`,
            fontError.message
          );
          console.warn(`   Path: ${font.json}`);
          console.warn(`   This asset is defined in assets.js but file is missing or inaccessible.`);
          // Return null to indicate failure but allow other assets to load
          return null;
        }
      });

      // Wait for ALL assets to finish loading (happens in parallel!)
      const results = await Promise.all([
        ...texturePromises,
        ...spinePromises,
        ...fontPromises,
      ]);

      // Count and log any failed assets
      const failedCount = results.filter((result) => result === null).length;
      if (failedCount > 0) {
        console.warn(
          `⚠️ Asset loading completed with ${failedCount} missing asset(s).`
        );
        console.warn(`   Check the warnings above for details.`);
        console.warn(`   Remove missing assets from src/assets.js or add the files.`);
      }

      return true;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const assetsSlice = createSlice({
  name: "assets",
  initialState: {
    status: "idle",
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadAssets.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(loadAssets.fulfilled, (state) => {
        state.status = "succeeded";
      })
      .addCase(loadAssets.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "An unknown error occurred.";
      });
  },
});

export default assetsSlice.reducer;
